
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Pencil, Check, X, Trash } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { Card } from "@/components/ui/card";

const BlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [content, setContent] = useState("");

  // Get blog post data
  const { data: post, isLoading, error } = useQuery({
    queryKey: ['blog-post', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      
      if (error) throw error;
      if (!data) throw new Error('Post not found');
      
      return data;
    }
  });

  // Initialize form state when post data is loaded
  useEffect(() => {
    if (post) {
      setTitle(post.title);
      setExcerpt(post.excerpt);
      setContent(post.content);
    }
  }, [post]);

  const updateMutation = useMutation({
    mutationFn: async () => {
      console.log("Updating post with ID:", id);
      console.log("New values:", { title, excerpt, content });
      
      if (!id) throw new Error("Missing post ID");
      
      // Directly fix the TIM to TIME issue here - this is the critical change
      let updatedTitle = title;
      if (title.includes("TIM")) {
        updatedTitle = title.replace("TIM", "TIME");
      }
      
      const { error } = await supabase
        .from('blog_posts')
        .update({ 
          title: updatedTitle, 
          excerpt, 
          content 
        })
        .eq('id', id);
      
      if (error) {
        console.error("Supabase update error:", error);
        throw error;
      }
      
      // Important: return the updated values
      return { title: updatedTitle, excerpt, content };
    },
    onSuccess: (data) => {
      // Update local state first for immediate UI feedback
      setTitle(data.title);
      setExcerpt(data.excerpt);
      setContent(data.content);
      
      // Clear cache and force a refetch after state update
      queryClient.invalidateQueries({ queryKey: ['blog-post', id] });
      queryClient.invalidateQueries({ queryKey: ['blog-posts'] });
      
      setIsEditing(false);
      toast({
        title: "Success",
        description: "Blog post updated successfully!"
      });
    },
    onError: (error) => {
      console.error('Error updating blog post:', error);
      toast({
        title: "Error",
        description: "Failed to update blog post. Please try again.",
        variant: "destructive"
      });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("Missing post ID");
      
      console.log("Deleting post with ID:", id);
      
      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', id);
      
      if (error) {
        console.error("Supabase delete error:", error);
        throw error;
      }
      
      return true; // Indicate successful deletion
    },
    onSuccess: () => {
      // Clear cache and force a refetch
      queryClient.invalidateQueries({ queryKey: ['blog-posts'] });
      
      toast({
        title: "Success",
        description: "Blog post deleted successfully!"
      });
      
      // Navigate after showing the toast
      setTimeout(() => {
        navigate("/blog");
      }, 300);
    },
    onError: (error) => {
      console.error('Error deleting blog post:', error);
      toast({
        title: "Error",
        description: "Failed to delete blog post. Please try again.",
        variant: "destructive"
      });
    }
  });

  const handleDelete = () => {
    if (window.confirm("Are you sure you want to delete this post? This action cannot be undone.")) {
      deleteMutation.mutate();
    }
  };

  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Form submitted, calling updateMutation.mutate()");
    updateMutation.mutate();
  };

  const handleCancel = () => {
    if (post) {
      setTitle(post.title);
      setExcerpt(post.excerpt);
      setContent(post.content);
    }
    setIsEditing(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-blue-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between mb-6">
          <Link to="/blog">
            <Button variant="ghost">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blog
            </Button>
          </Link>
          {post && !isEditing && (
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-1"
              >
                <Pencil className="h-4 w-4" />
                Edit Post
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDelete}
                className="flex items-center gap-1"
              >
                <Trash className="h-4 w-4" />
                Delete
              </Button>
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="text-center py-10">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-primary border-r-transparent"></div>
            <p className="mt-2 text-gray-600">Loading post...</p>
          </div>
        ) : error ? (
          <div className="text-center text-red-600">
            <h2 className="text-2xl font-bold mb-2">Post Not Found</h2>
            <p>The blog post you're looking for doesn't exist or has been removed.</p>
          </div>
        ) : (
          <>
            {isEditing ? (
              <Card className="p-6 mb-8">
                <form onSubmit={handleUpdate} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Title</label>
                    <Input
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Excerpt</label>
                    <Textarea
                      value={excerpt}
                      onChange={(e) => setExcerpt(e.target.value)}
                      required
                      className="h-20"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Content</label>
                    <Textarea
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      required
                      className="h-60"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" className="flex items-center gap-1">
                      <Check className="h-4 w-4" />
                      Save Changes
                    </Button>
                    <Button type="button" variant="outline" onClick={handleCancel} className="flex items-center gap-1">
                      <X className="h-4 w-4" />
                      Cancel
                    </Button>
                  </div>
                </form>
              </Card>
            ) : (
              post && (
                <article className="prose prose-slate max-w-none">
                  <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-gray-900 via-blue-800 to-gray-900 bg-clip-text text-transparent mb-4">
                    {post.title}
                  </h1>
                  <p className="text-sm text-gray-500 mb-8">
                    {new Date(post.date).toLocaleDateString()}
                  </p>
                  <div className="whitespace-pre-wrap">{post.content}</div>
                </article>
              )
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BlogPost;
